<template>
  <div
    v-if="!closed"
    ref="alertDiv"
    :class="['flex w-full py-4 sm:px-4 lg:px-5 sm:gap-3 lg:gap-4 min-h-14', backgroundColor]"
    :data-global-alert="type"
  >
    <div v-if="!!icon" class="flex-col items-start gap-2.5 inline-flex justify-evenly">
      <img :src="icon" :alt="`${type} icon`" class="w-[16px] h-[16px]" />
    </div>
    <RichText
      :field="{ value: content }"
      class="flex flex-col flex-grow !inline-block justify-evenly sm:mt-0.5"
      :class="['sm:text-m-bold lg:text-base-bold', foreColor]"
    />
    <div class="flex flex-col justify-evenly">
      <button @click="handleClose">
        <img :src="closeIcon" :alt="`${type} close icon`" class="w-[10px] h-[10px]" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { IGlobalAlert } from '@/interfaces/forms/global-alert.interface';
import { GlobalAlertTypes } from '@/enums/global-alerts.enum';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';

const props = defineProps<IGlobalAlert>();

const closed = ref(false);
const alertDiv = ref<HTMLDivElement>();

const emit = defineEmits<{
  (e: 'closeAlert', data: HTMLDivElement | undefined): void;
}>();

const iconMap = {
  information: require('@/assets/icons/info-icon-white.svg'),
  warning: require('@/assets/icons/warning-icon-black.svg'),
  error: require('@/assets/icons/danger-icon-white.svg'),
  default: require('@/assets/icons/info-icon-black.svg'),
};

const closeIconMap = {
  information: require('@/assets/icons/white-cross.svg'),
  warning: require('@/assets/icons/dark-cross.svg'),
  error: require('@/assets/icons/white-cross.svg'),
  default: require('@/assets/icons/dark-cross.svg'),
};

const foreColorMap = {
  information: 'text-racq-true-white [&>*]:text-racq-true-white',
  warning: 'text-racq-dark-grey  [&>*]:text-racq-dark-grey',
  error: 'text-racq-true-white [&>*]:text-racq-true-white',
  default: 'text-racq-dark-grey  [&>*]:text-racq-dark-grey',
};

const backgroundColorMap = {
  information: 'bg-racq-blue',
  warning: 'bg-racq-yellow',
  error: 'bg-racq-error-red',
  default: 'bg-racq-pale-blue',
};

const icon = computed(() => {
  let icon = iconMap[props.type?.toLowerCase() as GlobalAlertTypes];
  return icon ?? iconMap.default;
});

const closeIcon = computed(() => {
  let icon = closeIconMap[props.type?.toLowerCase() as GlobalAlertTypes];
  return icon ?? closeIconMap.default;
});

const foreColor = computed(() => foreColorMap[props.type?.toLowerCase() as GlobalAlertTypes]);

const backgroundColor = computed(() => {
  const type = props.type?.toLowerCase() as GlobalAlertTypes;
  let bgColour = backgroundColorMap[type];
  return bgColour ?? backgroundColorMap.default;
});

function handleClose() {
  closed.value = true;
  emit('closeAlert', alertDiv.value);
}
</script>
